import { api } from '../api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../utils/constants';

// Authentication service
export const authService = {
  // Login user
  login: async (mobile, password) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, {
        mobile: mobile?.toString().trim(),
        password: password?.toString().trim(),
      });

      if (response.status && response.code === 200) {
        console.log('AuthService Login Response:', response);
        return {
          success: true,
          data: {
            token: response.data.token,
            user: {
              id: response.data.user.user_id,
              user_id: response.data.user.user_id, // Add user_id for consistency
              admin_id: response.data.user.admin_id,
              full_name: response.data.user.full_name, // Add full_name for consistency
              name: response.data.user.full_name,
              email: response.data.user.email,
              mobileNumber: response.data.user.mobile_number,
              role: response.data.user.role_name,
              role_name: response.data.user.role_name, // Add role_name for normalization
              roleId: response.data.user.role_id,
              hospital_code: response.data.user.hospital_code,
              hospital_name: response.data.user.hospital_name,
              access_modules: response.data.user.access_modules || [],
            },
          },
        };
      } else {
        return {
          success: false,
          message: response.message || 'Login failed',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Login failed',
      };
    }
  },

  // Logout user
  logout: async () => {
    try {
      // Clear local storage
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Logout failed',
      };
    }
  },

  // Register user
  register: async (userData) => {
    try {
      // Remove confirmPassword from the data sent to API
      const { confirmPassword, ...apiData } = userData;

      const response = await api.post(API_ENDPOINTS.AUTH.REGISTER, apiData);
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Registration failed',
      };
    }
  },

  // Forgot password
  forgotPassword: async (mobile) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        mobile: mobile?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send reset code',
      };
    }
  },

  // Reset password
  resetPassword: async (mobile, otp, newPassword) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        mobile: mobile?.toString().trim(),
        otp: otp?.toString().trim(),
        new_password: newPassword,
      });
      return {
        success: response.status,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Password reset failed',
      };
    }
  },

  // Verify token
  verifyToken: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.AUTH.VERIFY_TOKEN);
      return {
        success: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Token verification failed',
      };
    }
  },

  // Refresh token — get a new token with fresh expiry
  refreshToken: async () => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN);
      if (response.status && response.data?.token) {
        return {
          success: true,
          data: {
            token: response.data.token,
            user: response.data.user,
          },
        };
      }
      return {
        success: false,
        message: response.message || 'Token refresh failed',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Token refresh failed',
      };
    }
  },

  // Verify OTP
  verifyOTP: async (mobile, otp) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
        mobile: mobile?.toString().trim(),
        otp: otp?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'OTP verification failed',
      };
    }
  },

  // Verify Registration OTP
  verifyRegistrationOTP: async (userData, otp) => {
    try {
      const { confirmPassword, ...apiData } = userData;
      const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_REGISTRATION, {
        ...apiData,
        otp: otp?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Registration verification failed',
      };
    }
  },

  // Resend OTP
  resendOTP: async (mobile) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.RESEND_OTP, {
        mobile: mobile?.toString().trim(),
      });
      return {
        success: response.status,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to resend OTP',
      };
    }
  },

  // Check if mobile number exists and whether first-time login
  checkMobile: async (mobile) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.CHECK_MOBILE, {
        mobile: mobile?.toString().trim(),
      });
      return {
        success: true,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to check mobile number',
      };
    }
  },

  // Send OTP for device verification
  sendDeviceOtp: async (mobile) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.SEND_DEVICE_OTP, {
        mobile: mobile?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send OTP',
      };
    }
  },

  // Verify device OTP
  verifyDeviceOtp: async (mobile, otp) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_DEVICE_OTP, {
        mobile: mobile?.toString().trim(),
        otp: otp?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'OTP verification failed',
      };
    }
  },

  // Set password for first-time users (after OTP verification)
  setPassword: async (mobile, password, confirmPassword, otp) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.SET_PASSWORD, {
        mobile: mobile?.toString().trim(),
        password,
        confirm_password: confirmPassword,
        otp: otp?.toString().trim(),
      });

      if (response.status && response.code === 200) {
        return {
          success: true,
          data: {
            token: response.data.token,
            user: {
              id: response.data.user.user_id,
              user_id: response.data.user.user_id,
              admin_id: response.data.user.admin_id,
              full_name: response.data.user.full_name,
              name: response.data.user.full_name,
              email: response.data.user.email,
              mobileNumber: response.data.user.mobile_number,
              role: response.data.user.role_name,
              role_name: response.data.user.role_name,
              roleId: response.data.user.role_id,
              hospital_code: response.data.user.hospital_code,
              hospital_name: response.data.user.hospital_name,
              access_modules: response.data.user.access_modules || [],
            },
          },
        };
      }
      return {
        success: false,
        message: response.message || 'Failed to set password',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to set password',
      };
    }
  },

  // Login with OTP (supports admin and patient roles)
  loginOtp: async (mobile, role = null) => {
    try {
      const payload = { mobile: mobile?.toString().trim() };
      if (role) payload.role = role;
      const response = await api.post(API_ENDPOINTS.AUTH.LOGIN_OTP, payload);
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send login OTP',
      };
    }
  },

  // Verify Login OTP (supports admin and patient roles)
  verifyLoginOtp: async (mobile, otp, role = null) => {
    try {
      const payload = {
        mobile: mobile?.toString().trim(),
        otp: otp?.toString().trim(),
      };
      if (role) payload.role = role;
      const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_LOGIN_OTP, payload);

      if (response.status && response.code === 200) {
        return {
          success: true,
          data: {
            token: response.data.token,
            user: {
              id: response.data.user.user_id,
              user_id: response.data.user.user_id,
              admin_id: response.data.user.admin_id,
              full_name: response.data.user.full_name,
              name: response.data.user.full_name,
              email: response.data.user.email,
              mobileNumber: response.data.user.mobile_number,
              role: response.data.user.role_name,
              role_name: response.data.user.role_name,
              roleId: response.data.user.role_id,
              hospital_code: response.data.user.hospital_code,
              hospital_name: response.data.user.hospital_name,
              access_modules: response.data.user.access_modules || [],
            },
          },
        };
      }
      return {
        success: false,
        message: response.message || 'Login OTP verification failed',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Login OTP verification failed',
      };
    }
  },

  // Register hospital admin - Step 1: Send OTP
  registerHospital: async (userData) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.REGISTER_HOSPITAL, {
        full_name: userData.full_name?.trim(),
        mobile_number: userData.mobile_number?.toString().trim(),
        email: userData.email?.trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send OTP',
      };
    }
  },

  // Verify hospital registration - Step 2: Submit all data with OTP
  verifyHospitalRegistration: async (formData) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_HOSPITAL_REGISTRATION, formData);
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Hospital registration failed',
      };
    }
  },

  // Aadhaar OTP - Send
  aadhaarSendOtp: async (aadhaarNumber) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.AADHAAR_SEND_OTP, {
        aadhaar_number: aadhaarNumber?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send Aadhaar OTP',
      };
    }
  },

  // Aadhaar OTP - Verify
  aadhaarVerifyOtp: async (clientId, otp, aadhaarNumber) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.AADHAAR_VERIFY_OTP, {
        client_id: clientId,
        otp: otp?.toString().trim(),
        aadhaar_number: aadhaarNumber?.toString().trim(),
      });
      return {
        success: response.status,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Aadhaar OTP verification failed',
      };
    }
  },

  // Check domain availability
  checkDomain: async (domain) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.AUTH.CHECK_DOMAIN}?domain=${encodeURIComponent(domain)}`);
      return {
        success: true,
        data: response.data,
        message: response.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to check domain',
      };
    }
  },
};
